import { MAX_POSTCODE_LENGTH } from '@/constants';
import { TransferToFundReason } from '@general/js/constants';

/**
 * Checks whether a string with spaces at start or end, or have sequential spaces in the middle
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string doesn't contain invalid spaces, false otherwise.
 */
export const stringWithInvalidSpace = (str) => {
  if (str !== undefined && str !== null && str !== '') {
    if (str.match(/\s$/) || str.match(/^\s/) || str.includes('  ')) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Checks whether a string has number and letters only
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string doesn't contain symbols, false otherwise.
 */
export const stringWithSymbols = (str) => {
  if (str !== undefined && str !== null && str !== '') {
    if (!str.match(/^[A-Z0-9]*$/)) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Checks whether a string has number, letters and space only
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string doesn't contain symbols, false otherwise.
 */
export const stringWithSymbolsNotSpace = (str) => {
  if (str !== undefined && str !== null && str !== '') {
    if (!str.match(/^[a-zA-Z0-9 ]*$/)) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Checks whether a string has number, letters, space, hyphen, forward slash or apostrophe only
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string doesn't contain symbols, false otherwise.
 */
export const stringWithInvalidSymbols = (str) => {
  if (str !== undefined && str !== null && str !== '') {
    if (!str.match(/^[a-zA-Z0-9\-'/ ]*$/)) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Checks whether a string is greater than the max character limit
 *
 * @param {string|undefined|null} str - The string to check.
 * @param {number|undefined|null} maxCharacters - The max character limit.
 * @return {boolean} - True if the passed-in string is greater than the max character limit.
 */
export const stringGreaterThanMaxCharacters = (str, maxCharacters) => {
  if (str !== undefined && str !== null && str !== '') {
    if (str.length > maxCharacters) {
      return false;
    }
    return true;
  }
  return true;
};

export const poBoxFormat = (str) => {
  if (str.toLowerCase().match(/\b(po\sbox)\s\d+\b/)) {
    return true;
  }
  return false;
};

/**
 * Checks whether a string has only these- 9 numbers, 1 ending-alphabet and as many separating hyphens or full stops
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string doesn't contain symbols, false otherwise.
 */
export const invalidConcessionCardNumber = (str) => {
  if (str !== undefined && str !== null && str !== '') {
    if (!str.match(/^(?:[.-]*\d[.-]*){9}[a-zA-Z]{1}$/)) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 * Function that prevents entry of certain characters into a numeric input field, to assist with some card number field.
 *
 * NOTE: To be used with an input field with type 'number'.
 *
 * @param {KeyboardEvent} event - The keyboard event.
 * @returns {boolean} - True if character entered is not 'e' and '+'.
 */
export const preventInvalidCharacterInput = (event) => {
  const CHAR_E = 69;
  const CHAR_PLUS_SIGN = 187;
  const bannedKeyCodes = [CHAR_E, CHAR_PLUS_SIGN];
  return bannedKeyCodes.includes(event.keyCode) ? event.preventDefault() : true;
};

/**
 * Function that prevents entry of certain characters into a numeric input field, to assist with some card number field.
 *
 * NOTE: To be used with an input field with type 'number'.
 *
 * @param {KeyboardEvent} event - The keyboard event.
 * @returns {boolean} - True if character entered is not 'e' and '+'.
 */
export const numberInputOnly = (event) => {
  const CHAR_E = 69;
  const CHAR_PLUS_SIGN = 187;
  const CHAR_MINUS_SIGN = 189;
  const bannedKeyCodes = [CHAR_E, CHAR_PLUS_SIGN, CHAR_MINUS_SIGN];
  return bannedKeyCodes.includes(event.keyCode) ? event.preventDefault() : true;
};

/**
 * Function that prevents entry of characters invalid for postcode.
 * Only allow input of numeric digits and maximum of 4 digits
 *
 * @param {KeyboardEvent} event - The keyboard event.
 * @returns {boolean} - Returns the value of numberInputOnly if input value is less than the MAX_POSTCODE_LENGTH, otherwise prevent default input behaviour.
 */
export const maxPostCodeLength = (event) => {
  if (event && event.target && event.target.value.length >= MAX_POSTCODE_LENGTH) {
    const charCode = event.keyCode ?? event.which;
    const charStr = String.fromCharCode(charCode);
    // Prevent default behaviour of input if numeric value
    // This is to ignore any additional character input when there are already 4 characters in the postcode field
    if (/\d/.test(charStr)) {
      return event.preventDefault();
    }
  }
  // Return numberInputOnly result by default to prevent non-numeric value inputs
  return numberInputOnly(event);
};

/**
 * @param {String} str The string to be validated
 * @returns {boolean} True if the string matches post code, false otherwise.
 */
export const validPostCode = (str) => {
  if (str) {
    return !!str.match(/^\d{4}$/);
  }
  return false;
};

/**
 * Validate the input with only numbers and white spaces.
 * @param {String} str The string to be validated.
 * @returns {boolean} True if the string only contains whitespaces and numbers.
 */
export const inputWithNumbersAndSpaces = (str) => {
  if (str) {
    return !!str.match(/^[0-9\s]+$/);
  }
  return false;
};

/**
 * Determine if New Fund Name is required for selected Termination Reason
 * @param {String} reason The termination reason value selected
 * @returns {boolean} True if fund name is required, otherwise False
 */
export const isFundNameRequired = (terminationReason) => {
  // Business rule: require fund name for following termination reasons
  return TransferToFundReason.includes(terminationReason);
};

/**
 * @param {String} str The string to be checked
 * @returns {boolean} True if the string starts with 'PO Box', false otherwise.
 */
export const isPOBoxAddress = (str) => {
  if (str) {
    return str.toLowerCase().startsWith('po box');
  }
  return false;
};
